import { FiX } from 'react-icons/fi';
import styled from 'styled-components';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { LayoutColors } from '@src/core/theme';
import { changeLocation } from '@src/core/store';
import { buyerSelector } from '@src/features/auth';
import { loadPendingOrders, pendingOrdersSelector } from '@src/features/order';
import { BodyLargeBold, BodyRegularSemiBold, BodySmallMedium } from '@src/fonts/typography/Typography';
import {
  useWidgetsTheme as theme,
  useAppDispatch,
  useAppSelector,
  useJSONLocalStorage,
  useTranslate,
  useWidgetParam,
} from '@src/core/hooks';
import { appendQueryParams } from '@src/common';

const notifyKey = '@planne/notify_current_sale_state';

const OpenPurchaseNotification = () => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const actionColor = theme('actionColor');
  const [display, setDisplay] = useState(false);
  const [, logged] = useAppSelector(buyerSelector);
  const accountPage = useWidgetParam('account-page');
  const checkoutPage = useWidgetParam('checkout-page');
  const doNotDisplayWhen = [checkoutPage, accountPage];
  const penddingSale = useAppSelector(pendingOrdersSelector);
  const { getItem, setItem } = useJSONLocalStorage(notifyKey, []);

  const handleFinishPurchase = () => {
    if (penddingSale) {
      if (penddingSale.length > 1) {
        dispatch(changeLocation(accountPage || ''));
        return;
      }

      const params = { orderId: penddingSale[0]!.id };
      const checkoutPageWithSale = checkoutPage ? appendQueryParams(checkoutPage, params) : undefined;

      if (checkoutPageWithSale) {
        dispatch(changeLocation(checkoutPageWithSale));
      }
    }
  };

  const handleCloseNotification = () => {
    const openSaleIds = penddingSale!.map(({ id }) => id);
    setItem(openSaleIds);
    setDisplay(false);
  };

  useEffect(() => {
    if (logged) {
      dispatch(loadPendingOrders());
    }
  }, [logged]);

  useLayoutEffect(() => {
    const saleIdsToSkipNotification = getItem() as string[];

    if (doNotDisplayWhen.includes(document.location.pathname)) {
      setDisplay(false);
      return;
    }

    if (penddingSale?.filter(({ id }) => !saleIdsToSkipNotification.includes(id)).length) {
      setDisplay(true);
    }
  }, [penddingSale?.length]);

  if (!display || !penddingSale?.length || !logged) return null;

  return (
    <Wrapper>
      <Container>
        <TitleContainer>
          <Title>{i18n('open_purchase_title')}</Title>
          <FiX cursor='pointer' size={16} color={LayoutColors.blackDark} onClick={handleCloseNotification} />
        </TitleContainer>
        <Description>{i18n('open_purchase_description')}</Description>
        <Button onClick={handleFinishPurchase} mainColor={actionColor}>
          {i18n('open_purchase_button_text')}
        </Button>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  position: absolute;
  right: 0px;
  top: 0px;
  z-index: 9999;
`;

const Container = styled('div')`
  display: flex;
  background: ${LayoutColors.whiteDark};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  box-shadow: 0px 3px 5px 2px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  width: 298px;
`;

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled('h2')`
  ${BodyLargeBold};
  color: ${LayoutColors.blackDark};
`;

const Description = styled('p')`
  ${BodySmallMedium};
  color: ${LayoutColors.blackMain};
`;

const Button = styled('button')<{ mainColor: string }>`
  ${BodyRegularSemiBold};
  color: ${LayoutColors.whiteLight};
  text-align: center;
  text-transform: uppercase;

  width: 100%;
  opacity: 1;
  padding: 12px;
  display: flex;
  border-radius: 4px;
  text-align: center;
  align-items: center;
  flex-direction: row;
  font-family: inherit;
  text-decoration: none;
  justify-content: center;
  transition: all 0.27s ease;
  border-width: 1px;
  border-style: solid;
  background: ${({ mainColor }) => mainColor};
  border-color: ${({ mainColor }) => mainColor};

  &:hover {
    opacity: 0.9;
  }
`;

export default OpenPurchaseNotification;
